import React, { useState, useEffect, useContext } from 'react';
import './select-search.scss';

import TextBox from 'components/text-box';
import { IOption } from 'domains/quotation/constants/information';
import { DataLayer } from 'libs/providers/datalayer/datalayer';
import {
  globalFlow,
  VIRTUAL_EVENT_SECTION,
  VIRTUAL_EVENT_SUB_ZONE,
  VIRTUAL_EVENT_TYPE_ELEMENT,
  VIRTUAL_EVENT_TYPE_EVENT,
  VIRTUAL_EVENT_TYPE_ZONE,
  VIRTUAL_EVENT_ZONE,
} from 'libs/providers/datalayer/constants';
import { AppContext } from 'domains/commons/contexts/global/global-provider';
import ArrowDownIcon from 'components/icons/arrow-down';

interface SelectSearchProps {
  label?: string;
  placeholder?: string;
  id: string;
  onChange: any;
  className: string;
  value?: string;
  required?: boolean;
  elements: IOption[];
  disabled?: boolean;
  dataLayer?: boolean;
  valueDatalayer?: string;
  editable?: boolean;
}

export const SelectSearch = ({
  label,
  placeholder,
  id,
  onChange,
  className,
  elements,
  value,
  required,
  disabled = false,
  dataLayer = false,
  valueDatalayer,
  editable = true,
}: SelectSearchProps) => {
  const [listOpen, setListOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const { store } = useContext(AppContext);

  useEffect(() => {
    setSearch(
      value ? elements.find((item) => item.value === value)?.label ?? '' : '',
    );
  }, [elements, value]);

  /*
   * validators
   */
  const [touched, setTouched] = useState(false);
  const validatorMsg =
    touched && required && !selected ? 'Debe seleccionar una opción' : '';
  /*
   * end validators
   */

  React.useEffect(() => {
    if (touched && required && !selected && dataLayer) {
      DataLayer.setVirtualEvent({
        tipoZona: VIRTUAL_EVENT_TYPE_ZONE.autos,
        zona: VIRTUAL_EVENT_ZONE.nuevo,
        subZona: VIRTUAL_EVENT_SUB_ZONE.inicio,
        sección: VIRTUAL_EVENT_SECTION.formulario,
        tipoEvento: VIRTUAL_EVENT_TYPE_EVENT.visualizar,
        tipoElemento: VIRTUAL_EVENT_TYPE_ELEMENT.modalMessageError,
        valor: `${valueDatalayer}Debe seleccionar una opción:${label}`,
        flujo: globalFlow(store.dataLayer),
      });
    }
  }, [touched, required, selected]);

  const listEditable = editable
    ? elements.filter((item: any) => {
        return item.label
          .toLowerCase()
          .includes((search ?? '').toLowerCase().trim());
      })
    : elements;

  return (
    <div
      id={`select-search-${id}`}
      className={`select-search ${className || ''} ${
        validatorMsg ? 'select-search--invalid' : ''
      }`}
    >
      <TextBox
        type="text"
        id={id}
        label={label}
        placeholder={placeholder}
        value={search}
        onChange={(e: any) => {
          if (editable) {
            setSearch(e.target.value);
          }
        }}
        onFocus={() => {
          !touched && setTouched(true);
          setListOpen(true);
        }}
        onBlur={() => setTimeout(() => setListOpen(false), 300)}
        classname="ui-input--full ui-input--mb0"
        disabled={disabled}
        rightIcon={<ArrowDownIcon />}
      />

      <p className="select-search__invalid_msg">{validatorMsg}</p>

      {elements.length && listOpen ? (
        <ul className="select-search__list">
          {listEditable.map((item: any) => (
            <li
              className="select-search__list__item"
              key={`select-search-${item.value}`}
              onClick={() => {
                onChange(item);
                setSelected(item);
                setSearch(item.label);
                setListOpen(false);
              }}
              role="presentation"
            >
              {item.label}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

SelectSearch.defaultProps = {
  value: null,
  required: false,
  placeholder: '',
  label: '',
};
